import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { SearchForMedicalPaOrdersFromPostgresParams } from "../../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import CustomTable from "../../../../components/custom-table/custom-table";
import DocumentScreenshotViewerModal from "../../../../components/modals/documentScreenshotViewerModal/documentScreenshotViewerModal";
import ImageViewerWithBadgesModal from "../../../../components/modals/imageViewerWithBadgesModal/imageViewerWithBadgesModal";
import RadioSelectModal from "../../../../components/modals/radioSelectModal/radioSelectModal";
import ViewReviewModal from "../../../../components/modals/ViewReviewModal/ViewReviewModal";
import { DOCUMENT_SCREENSHOT_VIEWER_TITLE } from "../../../../constants/stringLiterals";
import { useTablesContext } from "../../../../context/tablesContextProvider";
import { FilterValues } from "../../../../data-model/filterValues";
import { isHealthFacilityFeatureEnabled } from "../../../../data-model/healthFacility";
import { ScreenNamesForBigQuery } from "../../../../enums/eventsData";
import { ModalId } from "../../../../enums/modalId";
import { isExceptionFlowForUrologyOrg } from "../../../../enums/organizationTypes";
import { TableNames } from "../../../../enums/tableNames";
import { useExportTableData } from "../../../../hooks/useExportTableData";
import { useReviewIconClick } from "../../../../hooks/useReviewIconClick";
import { setFilter } from "../../../../redux/slice/filterValuesSlice";
import { closeModal } from "../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import { adjustTableHeadersForReviewMode } from "../../../../utils/adjustTableHeadersForReviewMode";
import { handleDocUploadScreenshotViewForOrdersForNewModalDesign } from "../../../../utils/handleDocUploadScreenshot";
import { getMedicalPaOrderRowTooltip } from "../../../../utils/medicalPaOrderRowTooltip";
import {
  handleNavigateToNextPage,
  handlePropagateEventOnClickInRpaStatusCell,
  handleRefetchPAOrderButtonClick,
  handleRefreshButtonClick,
  logOrderClickedEvent,
} from "../../../../utils/medicalPaOrdersTableHandlers";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { getFilterCreationConfig } from "../../hooks/filterCreationConfig";
import { useFilterOptionsGeneratorFromAlgolia } from "../../hooks/useFilterOptionsGeneratorFromAlgolia";
import { useBoStatusRadioSelect } from "../hooks/useBoStatusRadioSelect";
import { useFilterAndSearch } from "../hooks/useFilterAndSearch";
import { useInitialDataFetchingForTable } from "../hooks/useInitialDataFetchingForTable";
import { useMedicalPaOrdersTableData } from "../hooks/useMedicalPaOrdersTableData";
import { useModalOpenerForMedicalPaOrders } from "../hooks/useModalOpenerForMedicalPaOrders";
import { useNextPageDataFetch } from "../hooks/useNextPageDataFetch";
import { useSelectAll } from "../hooks/useSelectAll";
import { applySunstateUrologyFilterOverrides } from "../utils/applySunstateUrologyFilterOverrides";
import { getMedicalPaOrdersAlgoliaTableHeaders } from "../utils/medicalPaOrdersTable";

/**
 * Renders the medical PA orders worklist table with search, filtering, pagination,
 * bulk selection, export, and inline data refresh capabilities.
 * Adapts column headers and available actions based on the user's admin role,
 * and applies org-specific column overrides via
 * `getMedicalPaOrdersAlgoliaTableHeaders` (e.g. exception-flow orgs: Date of Work
 * + Location, Appointment Type, Estimate Value column, estimate radio-select modal
 * backed by `state.estimateOptions`, dropped RPA icon columns — same rules for
 * admin and non-admin). Includes the trailing status-log notes column
 * ({@link MedicalPaOrdersAlgoliaTableColumnKeys.SIDE_MODAL_ICON}) with the same refresh-on-close
 * behaviour as My PA Orders.
 *
 * Route-derived default facet filters are injected into every search payload
 * via `useDefaultFacetFiltersForRoute` (consumed inside
 * `useFilterPayload` / `useNextPageDataFetch` /
 * `useInitialDataFetchingForTable`). Currently this scopes the
 * `/orders-to-process` worklist to `master_auth_status: orders_to_process`
 * for every fetch — initial load, filter/search changes, and pagination.
 *
 * @param medicalPaOrdersTableRef - Ref for the table container element
 * @param onDataChange - Callback invoked with selected order IDs when checkbox selection changes
 * @param onComplete - When true, triggers a table data refresh
 * @param onBulkAssignButtonClick - Callback to open the bulk reassignment modal
 * @param exportingInProgress - Whether an export operation is currently in progress
 * @param onFilterPayloadChange - Receives the current search payload so the page header can export it
 */
const MedicalPaOrdersTable = ({
  medicalPaOrdersTableRef,
  onDataChange,
  onComplete,
  onBulkAssignButtonClick,
  onFilterPayloadChange,
}: {
  medicalPaOrdersTableRef: React.RefObject<HTMLDivElement>;
  onDataChange: (data: any) => void;
  onComplete: boolean;
  onBulkAssignButtonClick: () => void;
  exportingInProgress: boolean;
  onFilterPayloadChange?: (
    payload: SearchForMedicalPaOrdersFromPostgresParams | null,
  ) => void;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const orgId = getOrgIdForFetchExternalWorklist();
  const handleReviewIconClick = useReviewIconClick();

  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const { data: estimateOptions } = useSelector(
    (state: RootState) => state.estimateOptions,
  );
  const { data: singleHealthFacilityDetails } = useSelector(
    (state: RootState) => state.singleHealthFacilityDetails,
  );

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const { getTableDataFromContext } = useTablesContext();
  const tableAttributes = getTableDataFromContext(
    TableNames.MEDICAL_PA_ORDERS_NEW,
  );

  const {
    medicalPaOrdersState,
    setMedicalPaOrdersState,
    tableData,
    setTableData,
  } = useMedicalPaOrdersTableData();

  const {
    initialDataFetchingExecuted,
    setInitialDataFetchingExecuted,
    refetch: refetchMedicalPaOrders,
  } = useInitialDataFetchingForTable(setMedicalPaOrdersState, setTableData);

  const { openBoStatusModal, saveBoStatus } = useBoStatusRadioSelect({
    tableData,
    estimateOptions,
    onUpdated: refetchMedicalPaOrders,
  });

  const { exportData, loading: exportLoading } = useExportTableData();
  const { openedModalId, metaData } = useModalOpenerForMedicalPaOrders();

  const {
    searchTerm,
    setSearchTerm,
    paginationResetCount,
    validFilters,
    filterPayload,
  } = useFilterAndSearch(
    initialDataFetchingExecuted,
    setTableData,
    setMedicalPaOrdersState,
  );

  useEffect(() => {
    onFilterPayloadChange?.(filterPayload);
  }, [filterPayload, onFilterPayloadChange]);

  const { setEndIndex } = useNextPageDataFetch(
    validFilters,
    initialDataFetchingExecuted,
    searchTerm,
    medicalPaOrdersState,
    setMedicalPaOrdersState,
    setTableData,
  );

  const {
    handleSelectAll,
    toggleCheckboxSelection,
    disableBulkAssignButton,
    selectedCount,
    pagesForWhichSelectAllIsEnabled,
    setPagesForWhichSelectAllIsEnabled,
    isAdmin,
  } = useSelectAll(
    tableData,
    setTableData,
    medicalPaOrdersState,
    onDataChange,
    filterPayload,
  );

  const [isRefetchingPAOrders, setIsRefetchingPAOrders] = useState(false);
  const { filterData, isPostgresFacetResultsLoading } =
    useFilterOptionsGeneratorFromAlgolia(getFilterCreationConfig(orgId));
  const abortController = useRef<AbortController | null>(null);

  const filterSectionsData = useMemo(
    () => applySunstateUrologyFilterOverrides(filterData, orgId),
    [filterData, orgId],
  );

  const adjustedTableHeaders = useMemo(() => {
    const baseHeaders = getMedicalPaOrdersAlgoliaTableHeaders(
      isAdmin,
      getOrgIdForFetchExternalWorklist(),
    );
    return adjustTableHeadersForReviewMode(
      baseHeaders,
      isHealthFacilityFeatureEnabled(
        singleHealthFacilityDetails,
        "enable_review_mode",
      ),
    );
  }, [isAdmin, orgId, singleHealthFacilityDetails]);

  const onRefresh = () =>
    handleRefreshButtonClick(
      dispatch,
      setPagesForWhichSelectAllIsEnabled,
      setInitialDataFetchingExecuted,
    );

  useEffect(() => {
    if (onComplete) {
      onRefresh();
    }
  }, [onComplete]);

  return (
    <>
      <div ref={medicalPaOrdersTableRef} className="h-full">
        <CustomTable
          showBulkAssignButton={isAdmin}
          bulkAssignButtonDisabled={disableBulkAssignButton}
          bulkAssignSelectedCount={selectedCount}
          onBulkAssignButtonClick={onBulkAssignButtonClick}
          showRefetchPAOrderButton={isAdmin}
          refetchPAOrderButtonDisabled={
            disableBulkAssignButton || isRefetchingPAOrders
          }
          isRefetchingPAOrder={isRefetchingPAOrders}
          onRefetchPAOrderButtonClick={() =>
            handleRefetchPAOrderButtonClick(
              tableData,
              orgId,
              setIsRefetchingPAOrders,
            )
          }
          tableHeaders={adjustedTableHeaders}
          tableData={tableData}
          tableName={TableNames.MEDICAL_PA_ORDERS_NEW}
          itemsPerPage={20}
          pagesPerView={5}
          count={tableData.length ?? 0}
          totalCount={medicalPaOrdersState.data?.total_count ?? 0}
          endIndexOfTable={setEndIndex}
          searchingText={(searchText: string) => setSearchTerm(searchText)}
          hideSearchBar={false}
          onRowClick={(rowData: any) => {
            handleNavigateToNextPage(rowData, dispatch, navigate);
            logOrderClickedEvent(
              rowData,
              orgId,
              user?.id ?? "",
              user?.email ?? "",
            );
          }}
          isFetching={medicalPaOrdersState.loading}
          showExportButton={true}
          onExportButtonClick={() => {
            if (!filterPayload) return;
            exportData(
              { ...filterPayload, export: true },
              "medical-pa-orders-export.csv",
            );
          }}
          showInLineLoader={medicalPaOrdersState.showinLineLoader}
          showRefreshButton={true}
          onRefreshButton={onRefresh}
          isDisabled={medicalPaOrdersState.loading}
          onSelectAll={isAdmin ? handleSelectAll : undefined}
          toggleCheckboxSelection={
            isAdmin ? toggleCheckboxSelection : undefined
          }
          onComplete={onRefresh}
          showFilterButton={
            !medicalPaOrdersState.loading && !isPostgresFacetResultsLoading
          }
          filterSectionsData={filterSectionsData}
          onFilterApplyClicked={(data: FilterValues) =>
            dispatch(setFilter(data))
          }
          requestForPaginationReset={paginationResetCount}
          exportingInProgress={exportLoading}
          onPropagateEventOnClickInRpaStatusCell={(id: string) =>
            handlePropagateEventOnClickInRpaStatusCell(
              id,
              dispatch,
              authStatusOptions,
              abortController,
            )
          }
          onPropagateEventOnClickInRpaStatusTwoCell={(id: string) =>
            handleDocUploadScreenshotViewForOrdersForNewModalDesign(
              id,
              dispatch,
              abortController,
            )
          }
          onOverlappingIconClick={(icon_id: string, order_id: string) =>
            handleReviewIconClick(icon_id, order_id, abortController)
          }
          onSideModalClose={() =>
            void handleRefreshButtonClick(
              dispatch,
              setPagesForWhichSelectAllIsEnabled,
              setInitialDataFetchingExecuted,
            )
          }
          isCheckboxSelected={pagesForWhichSelectAllIsEnabled.includes(
            tableAttributes?.currentStartPage ?? 0,
          )}
          screenName={ScreenNamesForBigQuery.WORKLIST}
          shouldLogEvent={true}
          shouldDisableUiSorting={true}
          onClickableBadgeClick={
            isExceptionFlowForUrologyOrg(orgId) ? openBoStatusModal : undefined
          }
          getRowTooltip={(row) => getMedicalPaOrderRowTooltip(row)}
        />
      </div>
      {openedModalId === ModalId.IMAGE_VIEWER_WITH_BADGES_MODAL && (
        <ImageViewerWithBadgesModal
          tabs={metaData?.tabs ?? []}
          showLoading={metaData?.showLoading ?? false}
          title="Write Back Trail"
          onClose={() => {
            abortController.current?.abort();
            abortController.current = null;
          }}
        />
      )}
      {openedModalId === ModalId.DOCUMENT_SCREENSHOT_VIEWER_MODAL && (
        <DocumentScreenshotViewerModal
          title={DOCUMENT_SCREENSHOT_VIEWER_TITLE}
          onClose={() => {
            abortController.current?.abort();
            abortController.current = null;
          }}
        />
      )}
      {openedModalId === ModalId.VIEW_REVIEW_MODAL && (
        <ViewReviewModal
          onClose={() => {
            dispatch(closeModal());
            abortController.current?.abort();
            abortController.current = null;
          }}
          reviewData={metaData?.reviewData ?? null}
          isLoading={metaData?.isLoading ?? false}
        />
      )}
      {openedModalId === ModalId.RADIO_SELECT_MODAL && (
        <RadioSelectModal
          onSave={saveBoStatus}
          onClose={() => dispatch(closeModal())}
          onCancel={() => dispatch(closeModal())}
        />
      )}
    </>
  );
};

export default MedicalPaOrdersTable;
